import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';

export interface AddressRowProps {
  label: string;
  address: string;
  /** Optional explorer link override. Defaults to Solscan. */
  explorerUrl?: string;
  /** Hide the QR icon when this address shouldn't be used as a receive target. */
  showQr?: boolean;
  /** Optional warning rendered in the QR modal footer (replaces default copy). */
  warning?: string;
}

export default function AddressRow({
  label,
  address,
  explorerUrl,
  showQr = true,
  warning,
}: AddressRowProps) {
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const short = useMemo(() => {
    if (address.length <= 14) return address;
    return address.slice(0, 6) + '…' + address.slice(-6);
  }, [address]);

  const explorer = useMemo(
    () => explorerUrl ?? `https://solscan.io/account/${address}`,
    [explorerUrl, address],
  );

  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(address);
    } catch {
      // Older browsers / insecure contexts: fall back to the deprecated path.
      const ta = document.createElement('textarea');
      ta.value = address;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(ta);
      }
    }
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 1400);
  }

  function backdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) setQrOpen(false);
  }

  useEffect(() => {
    if (!qrOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setQrOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [qrOpen]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const modal = qrOpen ? (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm"
      onClick={backdrop}
    >
      <div className="w-[min(360px,calc(100vw-2rem))] rounded-xl border border-ink-700 bg-ink-900 shadow-2xl">
        <header className="flex items-center justify-between px-5 py-4 border-b border-ink-700">
          <h3 className="text-sm font-semibold text-ink-50">{label} address</h3>
          <button
            type="button"
            className="text-ink-400 hover:text-ink-50 transition-colors"
            aria-label="Close"
            onClick={() => setQrOpen(false)}
          >
            ✕
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div className="flex justify-center rounded-xl bg-white p-4">
            <QRCodeSVG value={address} size={240} level="M" marginSize={1} />
          </div>

          <div className="rounded-lg border border-ink-800 bg-ink-900/60 p-3 font-mono text-xs break-all text-ink-200 select-all">
            {address}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 rounded-lg bg-avo-500 px-4 py-2 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors"
              onClick={() => void copyAddress()}
            >
              {copied ? 'Copied!' : 'Copy address'}
            </button>
            <a
              href={explorer}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-2 text-sm text-ink-100 hover:bg-ink-800/60 transition-colors"
            >
              Solscan
            </a>
          </div>

          <p
            className={
              'text-[11px] leading-relaxed ' +
              (warning ? 'text-amber-400' : 'text-ink-500')
            }
          >
            {warning ??
              'This address is safe to share publicly. Anyone can send SOL or SPL tokens to it.'}
          </p>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-ink-500 shrink-0">{label}:</span>
      <span className="font-mono text-ink-200">{short}</span>
      <button
        type="button"
        className="rounded p-1 text-ink-400 hover:text-ink-50 hover:bg-ink-800 transition-colors"
        title={copied ? 'Copied!' : 'Copy address'}
        aria-label={`Copy ${label} address`}
        onClick={() => void copyAddress()}
      >
        {!copied ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-3.5 h-3.5"
          >
            <rect x={9} y={9} width={13} height={13} rx={2} ry={2} />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-3.5 h-3.5 text-avo-400"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </button>
      {showQr && (
        <button
          type="button"
          className="rounded p-1 text-ink-400 hover:text-ink-50 hover:bg-ink-800 transition-colors"
          title="Show QR code"
          aria-label={`Show QR code for ${label} address`}
          onClick={() => setQrOpen(true)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-3.5 h-3.5"
          >
            <rect x={3} y={3} width={7} height={7} rx={1} />
            <rect x={14} y={3} width={7} height={7} rx={1} />
            <rect x={3} y={14} width={7} height={7} rx={1} />
            <path d="M14 14h3v3h-3zM20 14h1M14 20h3M20 20h1" />
          </svg>
        </button>
      )}
      <a
        href={explorer}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded p-1 text-ink-400 hover:text-ink-50 hover:bg-ink-800 transition-colors"
        title="View on Solscan"
        aria-label={`View ${label} on Solscan`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-3.5 h-3.5"
        >
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1={10} y1={14} x2={21} y2={3} />
        </svg>
      </a>

      {modal && typeof document !== 'undefined'
        ? createPortal(modal, document.body)
        : null}
    </div>
  );
}
